import { useEffect, useState, type HTMLAttributes } from "react";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
  Autocomplete,
  // MenuItem,
} from "@mui/material";

import { getCategory } from "../utils/categories";
import { API_URL } from "../config/api";
import type { LicencePrefill } from "../models/LicencePrefill";
import {
  getClubs,
  normalizeClubText,
  type Club,
} from "../services/clubsService";
import { logActivity } from "../services/activity";

function renderClubOption(
  props: HTMLAttributes<HTMLLIElement> & { key?: string },
  club: Club
) {
  const { key, ...rest } = props;

  return (
    <li key={key} {...rest}>
      {club.number} — {club.name}
    </li>
  );
}

type Props = {
  open: boolean;
  onClose: () => void;
  onAdded: (emailSent: boolean) => void;
  initial?: LicencePrefill;
};

// Pour pré-remplir depuis une licence scannée, le parent remonte le
// composant (prop `key`) avec de nouvelles valeurs `initial`.
export default function AddJudokaDialog({
  open,
  onClose,
  onAdded,
  initial,
}: Props) {
  const [birthDate, setBirthDate] = useState(initial?.birthDate ?? "");
  const [licence, setLicence] = useState(initial?.licence ?? "");
  // Identifiant du QR code : permet au serveur de vérifier la licence.
  const [licencePublicId, setLicencePublicId] = useState(
    initial?.publicId ?? ""
  );
  // Pré-rempli depuis le listing de la fédération (n° de licence).
  const [fromListing, setFromListing] = useState(initial?.via === "listing");

  const [email, setEmail] = useState("");

  const [firstName, setFirstName] = useState(initial?.firstName ?? "");
  const [lastName, setLastName] = useState(initial?.lastName ?? "");

  const [gender, setGender] = useState<"M" | "F">(
    initial?.gender === "F" ? "F" : "M"
  );

  const [nationality, setNationality] = useState(
    initial?.nationality ?? ""
  );

  const [clubNumber, setClubNumber] = useState(initial?.clubNumber ?? "");
  const [clubName, setClubName] = useState(initial?.clubName ?? "");

  const [clubs, setClubs] = useState<Club[]>([]);

  useEffect(() => {
    getClubs().then(setClubs);
  }, []);

  const selectClub = (value: Club | string | null) => {
    if (value && typeof value !== "string") {
      setClubNumber(value.number);
      setClubName(value.name);
    }
  };

  const category = birthDate
    ? getCategory(new Date(birthDate).getFullYear())
    : "";

  const noCompetition = category === "U21+";

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const [submitting, setSubmitting] = useState(false);

  const canSubmit =
    !submitting &&
    emailValid &&
    !!lastName.trim() &&
    !!firstName.trim() &&
    !!gender &&
    !!nationality.trim() &&
    !!birthDate &&
    !noCompetition &&
    !!clubName.trim();

  const handleAdd = async () => {
  const registrationCode =
    localStorage.getItem("registrationCode");

  if (!registrationCode || submitting) {
    return;
  }

  setSubmitting(true);

  try {
    const response = await fetch(
      `${API_URL}/registration/${registrationCode}/judokas`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          licence,
          licencePublicId: licencePublicId || undefined,
          entryMode: fromListing ? "licence" : undefined,
          firstName,
          lastName,
          gender,
          nationality,
          birthDate,
          category,
          clubNumber,
          clubName,
        }),
      }
    );

    if (!response.ok) {
      throw new Error("Erreur API");
    }

    const created = await response.json();

    // Les champs propres au judoka sont vidés ; email, nom, nationalité
    // et club restent pour enchaîner facilement frères et sœurs.
    setFirstName("");
    setBirthDate("");
    setLicence("");
    setLicencePublicId("");
    setFromListing(false);

    onAdded(!!created.emailSent);

    onClose();

  } catch (err) {
    console.error(err);
  } finally {
    setSubmitting(false);
  }
};

  const handleCancel = () => {
    logActivity("add_cancelled");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>
        Ajouter un judoka
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              required
              type="email"
              label="Email"
              value={email}
              error={!!email && !emailValid}
              helperText={
                !!email && !emailValid
                  ? "Adresse email invalide"
                  : undefined
              }
              onChange={(e) => setEmail(e.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Licence (facultatif pour les judokas étrangers)"
              value={licence}
              onChange={(e) => setLicence(e.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              required
              label="Nom"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              required
              label="Prénom"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <RadioGroup row
              value={gender}
              onChange={(e) => setGender(e.target.value as "M" | "F")}
            >
              <FormControlLabel
                value="M"
                control={<Radio />}
                label="Garçon"
              />

              <FormControlLabel
                value="F"
                control={<Radio />}
                label="Fille"
              />
            </RadioGroup>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              required
              label="Nationalité"
              value={nationality}
              onChange={(e) =>
                setNationality(e.target.value)
              }
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              required
              type="date"
              label="Date de naissance"
              InputLabelProps={{
                shrink: true,
              }}
              value={birthDate}
              error={noCompetition}
              onChange={(e) =>
                setBirthDate(e.target.value)
              }
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              disabled
              label="Catégorie"
              value={category}
              error={noCompetition}
              helperText={
                noCompetition
                  ? "Aucune compétition disponible pour les U21+"
                  : undefined
              }
              sx={
                noCompetition
                  ? {
                      "& .MuiInputBase-input.Mui-disabled": {
                        WebkitTextFillColor: (theme) =>
                          theme.palette.error.main,
                      },
                      "& .MuiInputLabel-root.Mui-disabled, & .MuiFormHelperText-root.Mui-disabled":
                        {
                          color: "error.main",
                        },
                      "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                        {
                          borderColor: "error.main",
                        },
                    }
                  : undefined
              }
            />
          </Grid>

          {/* freeSolo : les suggestions viennent de la liste JudoWB,
              mais un club hors liste peut toujours être saisi à la main. */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Autocomplete
              freeSolo
              options={clubs}
              filterOptions={(options, { inputValue }) => {
                const query = inputValue.trim();
                return query
                  ? options
                      .filter((c) => c.number.startsWith(query))
                      .slice(0, 20)
                  : [];
              }}
              getOptionLabel={(option) =>
                typeof option === "string" ? option : option.number
              }
              renderOption={renderClubOption}
              inputValue={clubNumber}
              onInputChange={(_, value) => setClubNumber(value)}
              onChange={(_, value) => selectClub(value)}
              renderInput={(params) => (
                <TextField {...params} label="N° Club" />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Autocomplete
              freeSolo
              options={clubs}
              filterOptions={(options, { inputValue }) => {
                const query = normalizeClubText(inputValue);
                return query
                  ? options
                      .filter((c) =>
                        normalizeClubText(c.name).includes(query)
                      )
                      .slice(0, 20)
                  : [];
              }}
              getOptionLabel={(option) =>
                typeof option === "string" ? option : option.name
              }
              renderOption={renderClubOption}
              inputValue={clubName}
              onInputChange={(_, value) => setClubName(value)}
              onChange={(_, value) => selectClub(value)}
              renderInput={(params) => (
                <TextField {...params} required label="Nom du club" />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel}>
          Annuler
        </Button>

        <Button
          variant="contained"
          color="error"
          disabled={!canSubmit}
          onClick={handleAdd}
        >
          {submitting ? "Ajout en cours..." : "Ajouter"}
        </Button>
      </DialogActions>
    </Dialog >
  );
}